/** @format */

import { useRef, useState } from "react";
import { Brand } from "@question-bank/shared";
import { getAssetUrl } from "../api/questions";
import { uploadPdf } from "../api/papers";

type Props = {
	label: string;
	brand: Brand;
	assetKey?: string;
	onUpload: (assetKey: string) => void;
	onRemove: () => void;
};

/**
 * Upload / preview widget for a single PDF asset. Mirrors the presigned-upload
 * flow used by images but renders a filename + view link rather than an
 * `<img>` thumbnail.
 */
export const PdfUpload = ({ label, brand, assetKey, onUpload, onRemove }: Props) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [status, setStatus] = useState<"idle" | "uploading" | "error">("idle");

	const handleFile = async (file: File | undefined) => {
		if (!file) {
			return;
		}
		setStatus("uploading");
		try {
			const key = await uploadPdf(brand, file);
			onUpload(key);
			setStatus("idle");
		} catch {
			setStatus("error");
		}
	};

	return (
		<div className="flex flex-col gap-2">
			<span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</span>
			{assetKey ? (
				<div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
					<a
						href={getAssetUrl(brand, assetKey)}
						target="_blank"
						rel="noreferrer"
						className="truncate text-sm font-medium text-(--admin-color-primary-strong) underline"
					>
						View PDF
					</a>
					<button type="button" onClick={onRemove} className="text-sm font-semibold text-red-600 hover:text-red-700">
						Remove
					</button>
				</div>
			) : (
				<div className="flex items-center gap-3">
					<input
						ref={inputRef}
						type="file"
						accept="application/pdf"
						className="hidden"
						onChange={(event) => {
							void handleFile(event.target.files?.[0]);
							event.target.value = "";
						}}
					/>
					<button
						type="button"
						onClick={() => inputRef.current?.click()}
						disabled={status === "uploading"}
						className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
					>
						{status === "uploading" ? "Uploading…" : "Upload PDF"}
					</button>
					{status === "error" && <span className="text-sm text-red-600">Upload failed</span>}
				</div>
			)}
		</div>
	);
};
